import {
  generateContentToUserCultureMap,
  generateSupportPhoneNumbers,
} from "@/config/dialects.config";
import { DialectsEnum } from "@/constants/dialects.enum";
import { STAGE_CONTACT_NUMBER } from "@/constants/contact";

// Re-export for backwards compatibility
export { DialectsEnum, STAGE_CONTACT_NUMBER };

// WIDGET_CONTAINER_ID required to track the widget position, refer: setRootWidgetPosition in useElementEventsTracker.ts
export const WIDGET_CONTAINER_ID = "js-widgets-container";
export const PORTAL_BEFORE_CONTENT_ID = "js-portal-before-content";
export const HOME_VIRTUAL_LIST_CONTAINER_ID = "HOME_LAZY_LIST_CONTAINER";

export const ERROR_MESSAGES = {
  NO_GENRE_DATA_FOUND: "No genre data found",
  GENRE_DETAIL_API_ERROR: "Failed to load genre detail data",
};

export const SETTINGS_PAGE_SCROLLABLE_CONTAINER_ID =
  "js-settings-page-scrollable-container";

export const MAX_PROFILES = 5;

export const TV_SIDE_NAVIGATION = {
  MIN_WIDTH: "4.25rem",
  EXPANDED_WIDTH: "18rem", // Width when showing labels without overlay (e.g., genre page)
};

export const MICRO_DRAMA_SWIPE_ANIMATION_DURATION = 300;
export const MICRO_DRAMA_CONTROLS_HIDE_DELAY = 3000;

export enum MicrodramaVideoPositionEnum {
  PREVIOUS = "previous",
  CURRENT = "current",
  NEXT = "next",
}

export enum OrientationEnum {
  PORTRAIT = "portrait",
  LANDSCAPE = "landscape",
}

export enum EventsEnum {
  ORIENTATION_CHANGE = "orientationchange",
  CHANGE = "change",
}

// Type definitions for Screen Orientation API
export type OrientationLockType =
  | OrientationEnum.PORTRAIT
  | OrientationEnum.LANDSCAPE
  | "portrait-primary"
  | "portrait-secondary"
  | "landscape-primary"
  | "landscape-secondary"
  | "natural";

export interface ScreenOrientationWithLock extends ScreenOrientation {
  lock(orientation: OrientationLockType): Promise<void>;
  unlock(): void;
}

// Type definitions for iOS Safari video fullscreen
export interface HTMLVideoElementWithWebkit extends HTMLVideoElement {
  webkitEnterFullscreen(): void;
}

// Constants for fullscreen and orientation API property names
export const FULLSCREEN_API_PROPERTIES = {
  WEBKIT_ENTER_FULLSCREEN: "webkitEnterFullscreen",
  WEBKIT_END_FULLSCREEN: "webkitendfullscreen",
  LOCK: "lock",
  UNLOCK: "unlock",
} as const;

// todo: revert this to 300
export const CHECKOUT_PAGE_QR_CODE_EXPIRY_TIME_IN_SECONDS = 300;

export const CHECKOUT_OFFER_TIMER_DURATION = 5 * 60; // 300 seconds

export const MAX_AUTO_QUALITY_FOR_TV = 1080;
export const REMOTE_SEEK_INTERVAL_SECONDS = 30;
export const HOME_WIDGET_SKELETON_ITEMS_COUNT = 10;

export const CTA_SEEK_INTERVAL_SECONDS = 10;
export const CTA_SEEK_INTERVAL_SECONDS_FOR_TV = 20;
export const VIDEO_PLAYER_CURRENT_TIME_THROTTLE_MS = 1000;
export const VIDEO_PLAYER_SEEK_SYNC_DELAY_MS = 100;

export const TV_SEARCH_DEBOUNCE_TIME_IN_MS = 500;

export const canUserWatchContentOnMobile = true;

export const WIDGET_VIEW_EVENT_THRESHOLD = 0.1;

export const appRedirectionDeeplink =
  "https://tnpl-test.onelink.me/rkZ9/pntftzmd";

export const playStoreLink =
  "https://play.google.com/store/apps/details?id=in.stage&hl=en_IN&pli=1";
export const appStoreLink =
  "https://apps.apple.com/in/app/stage-boliyon-ki-kranti/id1531280099";

export const RUDDERSTACK_ENDPOINT = "https://rudder-event-prod.stage.in";

export const FACEBOOK_PIXEL_ENDPOINT = "https://connect.facebook.net";

export const SAATHI_SUPPORT_BASE_URL = "https://saathi4.stage.in/chat";

// session storage keys
export const overridingPlanIdKey = "overridingPlanId";
export const couponCodeKey = "couponCode";
export const failedPaymentRedirectionLink = "failedPaymentRedirectionLink";
export enum SessionStorageKeysEnum {
  CURRENCY = "currency",
  PAYING_PRICE = "payingPrice",
  REDIRECTION_URL_POST_LOGIN = "redirectionUrlPostLogin",
  PLAYBACK_POSITION_BEFORE_LOGIN = "playbackPositionBeforeLogin",
  WATCH_PROGRESS = "watchProgress",
  IS_TNLP_USER = "isTNPLUser",
  IS_INDEPENDENCE_DAY_OFFER = "isIndependenceDayOffer",
  IS_DIWALI_OFFER = "isDiwaliOffer",
  PLAN_IS_TRIAL = "planIsTrial",
  PLAN_VALID_TILL = "planValidTill",
  EDIT_PROFILE_ID = "editProfileId",
  CLIENT_IP = "client_ip",
  DEEPLINK = "deeplink",
  INITIAL_LANDING_URL = "initialLandingUrl",
  CUMULATIVE_WATCHTIME_SECONDS = "cumulative_watchtime_seconds",
  LAST_USED_PAYMENT_APP = "lastUsedPaymentApp",
  LAST_USED_PAYMENT_GATEWAY = "lastUsedPaymentGateway",
  LAST_USED_PAYMENT_TARGET_APP = "lastUsedPaymentTargetApp",
  STRIPE_PAYMENT_DETAILS = "stripe_payment_details",
}

export enum LocalStorageKeysEnum {
  SOURCE_CONTENT_ID_BEFORE_PAYING = "sourceContentIdBeforePaying",
  SOURCE_CONTENT_TYPE_BEFORE_PAYING = "sourceContentTypeBeforePaying",
  SOURCE_CONTENT_BEFORE_PAYING = "sourceContentBeforePaying",
  SOURCE_CONTENT_SLUG_BEFORE_PAYING = "sourceContentSlugBeforePaying",
  FIRST_CONTENT_SLUG_BEFORE_PAYING = "firstContentSlugBeforePaying",
  SOURCE_CONTENT_WATCH_URL_BEFORE_PAYING = "sourceContentWatchUrlBeforePaying",
  USER_CULTURE_SELECTION_SOURCE = "userCultureSelectionSource",
  GA_CLIENT_ID = "gaClientId",
  SHOULD_USE_HLS_265 = "shouldUseHLS265",
  CITY = "city",
  MICRO_DRAMA_DATA = "microDramaData",
  TITLE_CURRENT_TIME = "titleCurrentTime",
  CONTENT_DEEPLINK = "contentDeeplink",
  CLICKED_CONTENT_SLUGS = "clickedContentSlugs",
}

// checkout page
export const TRANSACTION_STATUS_API_CALL_TIMEOUT = 5 * 1000; // 5 seconds
export const PAYMENT_POLLING_DURATION = 5 * 60 * 1000;
export const PAYMENT_POLLING_GAP_DELAY = 2000;
export const PAYMENT_POLLING_COUNT =
  PAYMENT_POLLING_DURATION / PAYMENT_POLLING_GAP_DELAY;
export const SUCCESS_REDIRECTION_DELAY = 3000; // 3 seconds delay to show success message

// Country code defaults
export const DEFAULT_COUNTRY_CODE = "+91";
export const DEFAULT_COUNTRY = "IN";
export const DEFAULT_COUNTRY_NAME = "India";
export const INDIA_PHONE_NUMBER_LENGTH = 10;
export const MIN_MOBILE_NUMBER_LENGTH = 7;
export const MAX_MOBILE_NUMBER_LENGTH = 15;

// International payment defaults
export const INDIA_COUNTRY_CODE = "IN";
export const DEFAULT_INTERNATIONAL_CURRENCY = "USD";
export const DEFAULT_INTERNATIONAL_CURRENCY_SYMBOL = "$";
export const INTERNATIONAL_PLAN_ID = "annual_399";
export const INTERNATIONAL_TRIAL_PLAN_ID = "trial_stripe";
export const DEFAULT_INTERNATIONAL_PLAN_PRICE = 79;
export const DEFAULT_INTERNATIONAL_PLAN_VALIDITY = "Annual";
export const DEFAULT_INTERNATIONAL_PLAN_DAYS = 365;

// trial subscription
export const DEFAULT_TRIAL_DAYS = 7;

// subscription
export const DEFAULT_SUBSCRIPTION_MONTHS = 3;

// watch page
export const DEFAULT_POST_WATCH_ACTION_THRESHOLD = 60;
export const REMOVE_CONTENT_FROM_CONTINUE_WATCHING_LIST_THRESHOLD = 95;
export const ADD_CONTENT_TO_CONTINUE_WATCHING_LIST_THRESHOLD = 2;
export const LOGIN_REMINDER_TIMER_MINUTES = 30;

// Title completion thresholds (percentage of actual watchtime)
export const TITLE_COMPLETION_THRESHOLDS = {
  SHOW: 90, // 90% for episodes
  MOVIE: 80, // 80% for movies
} as const;

export enum SUBSCRIPTION_STATUS {
  NOT_SUBSCRIBED = 0,
  SUBSCRIBED = 1,
  SUBSCRIPTION_EXPIRED = 2,
}

export enum HttpStatusEnum {
  OK = 200,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  NOT_FOUND = 404,
  INTERNAL_SERVER_ERROR = 500,
}

export const DeviceTypeEnum = {
  MOBILE: "mobile",
  DESKTOP: "desktop",
};

export const SCREEN_WIDTH_BREAKPOINTS = {
  [DeviceTypeEnum.MOBILE]: {
    min: 0,
    max: 767,
  },
  [DeviceTypeEnum.DESKTOP]: {
    min: 768,
  },
};

export enum ContentTypeEnum {
  SHOW = "show",
  MOVIE = "individual",
}

export enum ShowFormatEnum {
  MICRO_DRAMA = "microdrama",
  STANDARD = "standard",
}

export const contentTypeUrlValue: Record<
  ContentTypeEnum | ShowFormatEnum.MICRO_DRAMA,
  string
> = {
  [ContentTypeEnum.SHOW]: "show",
  [ContentTypeEnum.MOVIE]: "movie",
  [ShowFormatEnum.MICRO_DRAMA]: "microdrama",
};

export const contentItemConfig = {
  DESKTOP: {
    width: 254,
    padding: 16,
  },
  MOBILE: {
    width: 131,
    padding: 16,
  },
  TV: {
    width: 230,
    padding: 16,
  },
};

export const responsiveRowItemClass =
  "relative aspect-[2/3] h-52 rounded-lg transition-transform duration-300 ease-out hover:scale-105 md:aspect-video md:h-44 bg-[var(--grey-4)]";

export const DEFAULT_PAGE_SIZE = 21; //7 rows of 3 columns;
export const DEFAULT_PAGE_SIZE_MOBILE = 9; //3 rows of 3 columns;

export const HORIZONTAL_INIFINTE_SCROLL_THRESHOLD = 8;

export enum ContentStatusEnum {
  COMING_SOON = "comingSoon",
}

export enum LangEnum {
  HI = "hi",
  EN = "en",
}

export enum LangApiKeyEnum {
  HI = "hin",
  EN = "en",
}

export const langDetails: {
  [key in LangEnum]: {
    label: string;
    apiKey: LangApiKeyEnum;
    enText: string;
  };
} = {
  [LangEnum.HI]: {
    label: "हिंदी",
    apiKey: LangApiKeyEnum.HI,
    enText: "Hindi",
  },
  [LangEnum.EN]: {
    label: "English",
    apiKey: LangApiKeyEnum.EN,
    enText: "English",
  },
};

export enum WidgetTypeEnum {
  // home page widgets
  PLATTER = "platter",
  CONTINUE_WATCHING = "continueWatching",
  TOP_20 = "top20",
  GENRE_CARD = "genreCard",
  NORMAL_ROW = "normalRow",
  CLICKABLE_ROW = "clickableRow",
  MICRO_DRAMA = "microDrama",
}

export enum ChildWidgetTypeEnum {
  THUMBNAIL = "thumbnail",
}

export enum ElementVisibilityEventType {
  THUMBNAIL = "thumbnail",
  WIDGET = "widget",
}

export enum OSFamilyEnum {
  ANDROID = "android",
  iOS = "iOS",
  LINUX = "linux",
  MACOS = "macos",
  OTHER = "other",
  WINDOWS = "windows",
}

export enum EnvironmentEnum {
  DEVELOPMENT = "dev",
  PRODUCTION = "production",
}

// Auto-generated from DIALECT_CONFIGS - adds new dialects automatically
export const supportPhoneNumbers: {
  [key in DialectsEnum]: string;
} = generateSupportPhoneNumbers();

export enum HorizontalThumbnailEnum {
  STANDARD = "ratio1", // aspect ratio 16/9
  WIDE_BANNER = "ratio3", // aspect ratio 1754/343
}

export enum PaymentAppEnum {
  GPAY = "gpay",
  OTHER = "other",
  PAYTM = "paytm",
  PHONEPE = "phonepe",
  QR_CODE = "qrCode",
}

export enum PaymentGatewayEnum {
  SETU = "SETU",
  JUSPAY = "JUSPAY",
  PHONEPE = "PHONEPE",
  PAYTM = "PAYTM",
  STRIPE = "STRIPE",
}

export enum PaymentInitiatedFromEnum {
  GENERAL = "general",
  MODAL = "modal",
}

export enum ModalCtaNameEnum {
  DISMISS = "dismiss",
  RETRY = "retry",
  STRIPE = "stripe",
}

export enum SubscriptionPaymentGatewayEnum {
  OLD_ONE_TIME_PAYMENT = "Old_One_Time_Payment",
}

export const paymentAppValues: {
  [key: string]: PaymentAppEnum;
} = {
  "com.google.android.apps.nbu.paisa.user": PaymentAppEnum.GPAY,
  "net.one97.paytm": PaymentAppEnum.PAYTM,
  "com.phonepe.app": PaymentAppEnum.PHONEPE,
};

export const paymentAppDisplayNames: Record<string, string> = {
  "com.google.android.apps.nbu.paisa.user": "Google Pay",
  "net.one97.paytm": "Paytm",
  "com.phonepe.app": "PhonePe",
};

export enum WindowEventListenersEnum {
  INITPAYMENTPOLLING = "initPaymentPolling",
  STOPPAYMENTPOLLING = "stopPaymentPolling",
  PAYMENT_POLLING_STARTED = "paymentPollingStarted",
  PAYMENT_STATUS_SUCCESS = "paymentStatusSuccess",
  PAYMENT_STATUS_FAILED = "paymentStatusFailed",
  TRIGGER_POST_WATCH_VIDEO = "triggerPostWatchVideo",
  INIT_TRUECALLER_LOGIN = "initTruecallerLogin",
  STOP_TRUECALLER_LOGIN_POLLING = "stopTruecallerLoginPolling",
  STATSIG_READY = "statsigReady",
  FETCH_MICRO_DRAMA_NEXT_PAGINATED_EPISODES = "fetchMicroDramaNextPaginatedEpisodes",
  SHOW_PAYMENT_FAILED_BANNER = "showPaymentFailedBanner",
  SHOW_PAYMENT_RETRY_MODAL = "showPaymentRetryModal",
}

export enum MandateTransactionStatusEnum {
  FAILED = "failed",
  PENDING = "pending",
  SUCCESS = "success",
}

export enum MasterMandateStatusEnum {
  MANDATE_ACTIVE = "mandate_active",
  MANDATE_CANCELLED_AND_STARTED_ANEW = "mandate_cancelled_and_started_anew",
  MANDATE_FAILED = "mandate_failed",
  MANDATE_INITIATED = "mandate_initiated",
  MANDATE_MIGRATED = "mandate_migrated", // Mandate migrated from JUSPAY to Master Mandate
  MANDATE_PAUSED = "mandate_paused",
  MANDATE_PAUSED_IN_APP = "mandate_paused_in_app",
  MANDATE_PAUSED_NO_APP_OPEN = "mandate_paused_no_app_open",
  MANDATE_PAUSED_PSP = "mandate_paused_psp",
  MANDATE_PAUSED_RETRIES_EXHAUSTED = "mandate_paused_retries_exhausted", //Max number of mandate retries exhausted
  MANDATE_REVOKED_IN_APP = "mandate_revoked_in_app",
  MANDATE_REVOKED_PSP = "mandate_revoked_psp",
}

export enum UserSubscriptionStatusEnum {
  ACTIVE = "active",
  CANCELLED = "cancelled",
  EXPIRED = "expired",
  PAUSED = "paused",
  SUSPENDED = "suspended",
  TERMINATED = "terminated",
  UNSUBSCRIBED = "unsubscribed",
}
export enum SubscriptionStatusEnum {
  NON_SUBSCRIBER = "non_subscriber",
  // NON_TRIAL = 'non_trial',
  SUBSCRIPTION_ACTIVE = "subscription_active",
  SUBSCRIPTION_OVER = "subscription_over",
  TRIAL_ACTIVE = "trial_active",
  TRIAL_OVER = "trial_over",
}
// Mandate paused type for differentiating trial vs subscription mandate paused
export enum MandatePausedTypeEnum {
  TRIAL = "trial",
  SUBSCRIPTION = "subscription",
  NONE = "none",
}
// English labels for user-visible subscription status
// Note: Do not confuse with the enum below that is used for renewal banners
export enum SubscriptionStatusUiLabelEnum {
  NON_SUBSCRIBER = "Unsubscribed",
  SUBSCRIPTION_ACTIVE = "Subscribed",
  SUBSCRIPTION_OVER = "Unsubscribed",
  TRIAL_ACTIVE = "Trial Active",
  TRIAL_OVER = "Trial Paused",
}

// Returns localized label for subscription status based on language
export const getSubscriptionStatusLabel = (
  status: SubscriptionStatusEnum,
  lang: LangEnum,
): string => {
  const enMap: Record<SubscriptionStatusEnum, string> = {
    [SubscriptionStatusEnum.SUBSCRIPTION_ACTIVE]:
      SubscriptionStatusUiLabelEnum.SUBSCRIPTION_ACTIVE,
    [SubscriptionStatusEnum.SUBSCRIPTION_OVER]:
      SubscriptionStatusUiLabelEnum.SUBSCRIPTION_OVER,
    [SubscriptionStatusEnum.TRIAL_ACTIVE]:
      SubscriptionStatusUiLabelEnum.TRIAL_ACTIVE,
    [SubscriptionStatusEnum.TRIAL_OVER]:
      SubscriptionStatusUiLabelEnum.TRIAL_OVER,
    [SubscriptionStatusEnum.NON_SUBSCRIBER]:
      SubscriptionStatusUiLabelEnum.NON_SUBSCRIBER,
  };

  const hiMap: Record<SubscriptionStatusEnum, string> = {
    [SubscriptionStatusEnum.SUBSCRIPTION_ACTIVE]: "सब्सक्रिप्शन चालू है",
    [SubscriptionStatusEnum.SUBSCRIPTION_OVER]: "सब्सक्रिप्शन नहीं लिया है",
    [SubscriptionStatusEnum.TRIAL_ACTIVE]: "ट्रायल चालू है",
    [SubscriptionStatusEnum.TRIAL_OVER]: "ट्रायल पॉज ह",
    [SubscriptionStatusEnum.NON_SUBSCRIBER]: "सदस्यता नहीं",
  };

  return (lang === LangEnum.HI ? hiMap : enMap)[status];
};
export const userSubscriptionStatusValues: {
  [key in UserSubscriptionStatusEnum]: SUBSCRIPTION_STATUS;
} = {
  [UserSubscriptionStatusEnum.ACTIVE]: SUBSCRIPTION_STATUS.SUBSCRIBED,
  [UserSubscriptionStatusEnum.CANCELLED]:
    SUBSCRIPTION_STATUS.SUBSCRIPTION_EXPIRED,
  [UserSubscriptionStatusEnum.EXPIRED]:
    SUBSCRIPTION_STATUS.SUBSCRIPTION_EXPIRED,
  [UserSubscriptionStatusEnum.PAUSED]: SUBSCRIPTION_STATUS.SUBSCRIPTION_EXPIRED,
  [UserSubscriptionStatusEnum.SUSPENDED]:
    SUBSCRIPTION_STATUS.SUBSCRIPTION_EXPIRED,
  [UserSubscriptionStatusEnum.TERMINATED]:
    SUBSCRIPTION_STATUS.SUBSCRIPTION_EXPIRED,
  [UserSubscriptionStatusEnum.UNSUBSCRIBED]: SUBSCRIPTION_STATUS.NOT_SUBSCRIBED,
};

export enum UseTrackingAnalyticsSendToEnum {
  FIREBASE = "firebase",
  RUDDERSTACK = "rudderstack",
  FACEBOOK = "facebook",
  APPSFLYER = "appsflyer",
}
export enum RecurringPaymentStatusEnum {
  PAUSED = "paused",
  SCHEDULED = "recurring-payment-scheduled",
  CANCELLED = "cancelled",
  TRIGGERED = "recurring-payment-triggered",
  MANDATE_ACTIVE = "notification_scheduled",
  MANDATE_PAUSED = "notification_paused",
}

export const headerNavigationItems = [
  {
    label: "common.movies",
    href: "/movies",
  },
  {
    label: "common.shows",
    href: "/shows",
  },
  {
    label: "common.myList",
    href: "/my-list",
  },
];

export const contentTypeMetaTitleValues = {
  [LangEnum.HI]: {
    [contentTypeUrlValue[ContentTypeEnum.SHOW]]: "वेब सीरीज़",
    [contentTypeUrlValue[ContentTypeEnum.MOVIE]]: "फिल्म",
  },
  [LangEnum.EN]: {
    [contentTypeUrlValue[ContentTypeEnum.SHOW]]: "Web Series",
    [contentTypeUrlValue[ContentTypeEnum.MOVIE]]: "Movie",
  },
};

//  _
// | |
// | | _____ _   _ ___
// | |/ / _ \ | | / __|
// |   <  __/ |_| \__ \
// |_|\_\___|\__, |___/
//            __/ |
//           |___/
export enum AppNameEnum {
  SAMSUNG_TV_APP = "Samsung TV App",
  LG_TV_APP = "Lg TV App",
  STAGE_WEBSITE = "Stage Website",
}

// remote config keys
export const enum RemoteConfigEnum {
  DD_SAMPLING_RATE = "DD_SAMPLING_RATE",
  DD_SESSION_REPLAY_RATE = "DD_SESSION_REPLAY_RATE",
  PLAYBACK_PULSE_INTERVAL_SECONDS = "PLAYBACK_PULSE_INTERVAL_SECONDS",
}

export const remoteConfigDefaultValues: {
  [k in RemoteConfigEnum]: string | number | boolean;
} = {
  [RemoteConfigEnum.DD_SAMPLING_RATE]: 5,
  [RemoteConfigEnum.DD_SESSION_REPLAY_RATE]: 0,
  [RemoteConfigEnum.PLAYBACK_PULSE_INTERVAL_SECONDS]: 30,
};

export enum CookiesEnum {
  Lang = "LANG",
  Dialect = "DIALECT",
  Token = "TOKEN",
  Status = "STATUS",
  UserData = "USER_DATA",
  TrialEligibility = "TRIAL_ELIGIBILITY",
  SubscriptionPlanId = "SUBSCRIPTION_PLAN_ID",
  DayType = "DAY_TYPE",
  UserCreatedAt = "User_CREATED_AT",
  UserCulture = "USER_CULTURE",
  TempCulture = "TEMP_CULTURE", // Used to preselect the culture in the User Selection Popup when a user lands directly on a content detail page via a deeplink.
  InitialUserCulture = "INITIAL_USER_CULTURE",
  Profile = "PROFILE",
  DeviceId = "DEVICE_ID",
  PaywallContentId = "PAYWALL_CONTENT_ID",
  PaywallContentType = "PAYWALL_CONTENT_TYPE",
  SpecialAccessContentIds = "SPECIAL_ACCESS_CONTENT_IDS",
  SpecialAccessConsumptionStateCategory = "SPECIAL_ACCESS_CONSUMPTION_STATE_CATEGORY",
  SpecialAccessConsumptionStateValue = "SPECIAL_ACCESS_CONSUMPTION_STATE_VALUE",
  SpecialAccessWatchPageUrl = "SPECIAL_ACCESS_WATCH_PAGE_URL",
  SpecialAccessDialect = "SPECIAL_ACCESS_DIALECT",
  FreeWatchFlow = "FREE_WATCH_FLOW",
  FreeWatchTimeExceeded = "FREE_WATCH_TIME_EXCEEDED",
  MandatePausedType = "MANDATE_PAUSED_TYPE",

  FBP = "FBP", // for Facebook Pixel
  FBC = "FBC", // for Facebook Pixel
  TrialPlanId = "TRIAL_PLAN_ID",
  TrialUpdatePlanForVersionBelow = "TRIAL_UPDATE_PLAN_FOR_VERSION_BELOW",
  TrialIsDeeplinkPriority = "TRIAL_IS_DEEPLINK_PRIORITY",
  ShowCrossIcon = "SHOW_CROSS_ICON",
  ShowTCByDefault = "SHOW_TC_BY_DEFAULT",
  IsPaywallAssetClick = "IS_PAYWALL_ASSET_CLICK",
  ShowNewPaymentSuccessPage = "SHOW_NEW_PAYMENT_SUCCESS_PAGE",
  RedirectionToApp = "REDIRECTION_TO_APP",
  MandateStatus = "MANDATE_STATUS",
  SubscriptionStatus = "SUBSCRIPTION_STATUS",
  TrialDaysElapsed = "TRIAL_DAYS_ELAPSED",
  PaywallPage = "PAYWALL_PAGE",
  STWClaimTime = "STW_CLAIM_TIME",
  IsWebview = "IS_WEBVIEW",
  EnableTimer = "ENABLE_TIMER",
  ShowCrossIconPaymentSuccess = "SHOW_CROSS_ICON_PAYMENT_SUCCESS",
  GamificationClaimTime = "GAMIFICATION_CLAIM_TIME",
  UserCountry = "USER_COUNTRY",
  IsGlobalPlan = "IS_GLOBAL_PLAN",
  ShowPaymentRetryModal = "SHOW_PAYMENT_RETRY_MODAL",
}

export const cookiesValues: {
  [k in CookiesEnum]: string;
} = {
  [CookiesEnum.Lang]: "lang",
  [CookiesEnum.Dialect]: "dialectv1",
  [CookiesEnum.Token]: "tokenv1",
  [CookiesEnum.Status]: "statusv1",
  [CookiesEnum.UserData]: "userDatav1",
  [CookiesEnum.TrialEligibility]: "trialEligibility",
  [CookiesEnum.SubscriptionPlanId]: "subscriptionPlanId",
  [CookiesEnum.DayType]: "dayType",
  [CookiesEnum.UserCreatedAt]: "userCreatedAt",
  [CookiesEnum.UserCulture]: "userCulture",
  [CookiesEnum.TempCulture]: "tempCulture",
  [CookiesEnum.InitialUserCulture]: "initialUserCulture",
  [CookiesEnum.Profile]: "profile",
  [CookiesEnum.DeviceId]: "deviceId",
  [CookiesEnum.PaywallContentId]: "paywallContentId",
  [CookiesEnum.PaywallContentType]: "paywallContentType",
  [CookiesEnum.SpecialAccessContentIds]: "specialAccessContentIds",
  [CookiesEnum.SpecialAccessConsumptionStateCategory]:
    "specialAccessConsumptionStateCategory",
  [CookiesEnum.SpecialAccessConsumptionStateValue]:
    "specialAccessConsumptionStateValue",
  [CookiesEnum.SpecialAccessWatchPageUrl]: "specialAccessWatchPageUrl",
  [CookiesEnum.SpecialAccessDialect]: "specialAccessDialect",
  [CookiesEnum.MandateStatus]: "mandateStatus",
  [CookiesEnum.SubscriptionStatus]: "subscriptionStatus",
  [CookiesEnum.TrialDaysElapsed]: "trialDaysElapsed",
  [CookiesEnum.PaywallPage]: "paywallPage",
  [CookiesEnum.STWClaimTime]: "stwClaimTime",

  [CookiesEnum.FBP]: "_fbp", // for Facebook Pixel
  [CookiesEnum.FBC]: "_fbc", // for Facebook Pixel

  // For statsig cookies, please use "stsg" prefix in the value
  [CookiesEnum.TrialPlanId]: "stsgTrialPlanId",
  [CookiesEnum.TrialUpdatePlanForVersionBelow]:
    "stsgTrialUpdatePlanForVersionBelow",
  [CookiesEnum.TrialIsDeeplinkPriority]: "stsgTrialIsDeeplinkPriority",
  [CookiesEnum.ShowCrossIcon]: "stsgShowCrossIcon",
  [CookiesEnum.ShowTCByDefault]: "stsgShowTCByDefault",
  [CookiesEnum.IsPaywallAssetClick]: "stsgIsPaywallAssetClick",
  [CookiesEnum.ShowNewPaymentSuccessPage]: "stsgShowNewPaymentSuccessPage",
  [CookiesEnum.RedirectionToApp]: "stsgRedirectionToApp",
  [CookiesEnum.FreeWatchFlow]: "freeWatchFlow",
  [CookiesEnum.IsWebview]: "isWebview",
  [CookiesEnum.FreeWatchTimeExceeded]: "freeWatchTimeExceeded",
  [CookiesEnum.MandatePausedType]: "mandatePausedType",
  [CookiesEnum.EnableTimer]: "stsgEnableTimer",
  [CookiesEnum.ShowCrossIconPaymentSuccess]: "stsgShowCrossIconPaymentSuccess",
  [CookiesEnum.GamificationClaimTime]: "gamificationClaimTime",
  [CookiesEnum.UserCountry]: "userCountry",
  [CookiesEnum.IsGlobalPlan]: "isGlobalPlan",
  [CookiesEnum.ShowPaymentRetryModal]: "stsgShowPaymentRetryModal",
};
export enum SubscriptionStatusLabelEnum {
  NON_TRIAL = "Non-Trial",
  TRIAL_ACTIVATED = "Trial Active",
  TRIAL_CHURNED = "Trial Paused",
  SUBSCRIPTION_ACTIVE = "Subscribed",
  SUBSCRIPTION_EXPIRED = "Unsubscribed",
}
export const cookiesPreservedOnLogout = [
  CookiesEnum.Dialect,
  CookiesEnum.UserCulture,
  CookiesEnum.Lang,
  CookiesEnum.DeviceId,
  CookiesEnum.MandateStatus,
  CookiesEnum.SubscriptionStatus,
  CookiesEnum.SpecialAccessConsumptionStateCategory,
  CookiesEnum.SpecialAccessWatchPageUrl,
  CookiesEnum.SpecialAccessDialect,
  CookiesEnum.UserCountry,
];

export enum WindowEventListeners {
  SHOW_CONTENT_DETAIL_APP_DOWNLOAD_POPUP = "SHOW_CONTENT_DETAIL_APP_DOWNLOAD_POPUP",
}

export enum URLQueryParamsEnum {
  TV_CODE = "tvCode",
  UTM_SOURCE = "utm_source",
  UTM_CAMPAIGN = "utm_campaign",
  UTM_MEDIUM = "utm_medium",
  SHOW_USER_CULTURE_POPUP = "showUserCulturePopup",
  /**
   * When skipPaywall=true and an unauthenticated user clicks "Start Watching" on the content detail page:
   * 1. Skip the paywall screen
   * 2. Redirects to the login page
   * 3. After successful login, redirects directly to the watch page
   */
  SKIP_PAYWALL = "skipPaywall",
  /**
   * Another reengagement expirement to direclty allow users to watch content without login
   * then after X min will redirect to login page and post login will redirect to the watch page
   */
  WATCH_WITHOUT_LOGIN = "watchWithoutLogin",
  PLAY_FROM_BEGINNING = "playFromBeginning",
  /**
   * URL parameter to specify where to redirect the user after successful login
   */
  REDIRECTION_AFTER_LOGIN_TO = "redirectionAfterLoginTo",

  // ONLY FOR TV 👇:
  // Used to override the back button behavior on the Checkout page when the user navigates directly from the login page
  DIRECLTY_FROM_LOGIN = "directlyFromLogin",
  isDiwaliOffer = "isDiwaliOffer",

  // we are unable to mock user agent in cypress, so we are using this query param to determine if the request is coming from a mobile device
  // to navigate to the microdrama page, else it will redirect to the 404 page
  MOBILE = "mobile",

  CONTENT_SLUG = "contentSlug",
  FROM_CONTENT_DETAIL_PAGE = "fromContentDetailPage",
  /**
   * URL parameter to specify where to redirect the user after successful payment
   * Used in the checkout page to redirect the user to the content detail page after successful payment
   * Example: /checkout?redirectionAfterPaymentTo=/content/123
   */
  REDIRECTION_AFTER_PAYMENT_TO = "redirectionAfterPaymentTo",

  /**
   * URL parameter to specify the content id to redirect the user to
   * Used in the checkout page to redirect the user to the content detail page after successful payment
   * Example: /checkout?contentId=123
   */
  CONTENT_ID = "contentId",

  /**
   * URL parameter for access token in character chat
   * Example: /characters?accesstoken=xyz
   */
  ACCESS_TOKEN = "accesstoken",

  /**
   * URL parameter for character slug in character chat
   * Example: /characters/chat?c=character-slug
   */
  CHARACTER = "c",

  /**
   * URL parameter to specify the content type to redirect the user to
   * Used in the checkout page to redirect the user to the content detail page after successful payment
   * Example: /checkout?contentType=movie
   * refer ContentTypeEnum for the possible values
   */
  CONTENT_TYPE = "contentType",

  /**
   * URL parameter to specify if the user is coming from the web to the app
   * Used in the app open redirection deeplink to redirect the user to the content detail page on the app
   */
  STITCHING_WEB = "stitchingWeb",

  /**
   * URL parameter to indicate the user is in the Tambola flow.
   * When isTambolaFlow=true, after successful login the user is redirected
   * to the returnUrl with their userId appended as a query param.
   */
  IS_TAMBOLA_FLOW = "isTambolaFlow",

  /**
   * URL parameter specifying the return URL for the Tambola flow.
   * After login, the user is redirected to this URL with userId appended.
   */
  RETURN_URL = "returnUrl",

  /**
   * URL parameter to pass userId for watchWithoutLogin flow.
   * When watchWithoutLogin=true and a userId is provided via query param,
   * it is used for event tracking in place of the cookie-based userId.
   */
  USER_ID = "userId",
}

export const enum DayTypeValueEnum {
  DAY_0 = "D0",
  DAY_N = "Dn",
}

export enum LoginTypeEnum {
  QR_CODE = "login_qr_code",
  PHONE_NUMBER = "phone_number",
  TRUECALLER = "truecaller",
  QR_CODE_REFRESH = "qr_code_refresh",
}

export enum DeviceScreenTypeEnum {
  MOBILE = "Mobile",
  TABLET = "Tablet",
  DESKTOP = "Desktop",
}

export enum PlanTypeEnum {
  QUARTERLY = "Quarterly",
  WEEKLY = "weekly",
}

export enum ParentPostMessageEnum {
  CLOSE_APP = "close-app",
}

export enum DataAttributesValuesEnum {
  SKELETON = "skeleton",
}

export enum PlatformEnum {
  TV = "tv",
  WEB = "web",
  WEBVIEW = "webview",
}

//  _____   _____ _ __ | |_ ___
// / _ \ \ / / _ \ '_ \| __/ __|
// | __/\ V /  __/ | | | |_\__ \
// \___| \_/ \___|_| |_|\__|___/

export const TITLE_START_EVENT_THRESHOLD_SECONDS = 60;

export enum DynamicEventNamesPlaceholders {
  USER_CULTURE = "%user_culture%",
}

export enum EventNamesEnum {
  APP_DOWNLOAD_BANNER_CLICKED = "app_download_banner_clicked",
  APP_OPEN_DRAWER_OPENED = "app_open_drawer_opened",
  APP_OPEN_DRAWER_CLICKED = "app_open_drawer_clicked",
  FULL_SCREEN_CLICKED = "full_screen_clicked",
  LOGIN_PAGE_VIEWED = "login_page_viewed",
  LOGIN_CODE_GENERATED = "login_code_generated",
  START_WATCHING_CLICKED = "start_watching_clicked",
  EPISODE_TILE_CLICKED = "episode_tile_clicked",
  TRAILER_PLAY_CTA_CLICKED = "trailer_play_cta_clicked",
  SIMILAR_CONTENT_CLICKED = "similar_content_clicked",
  PLAYBACK_PULSE = "playback_pulse",
  PLAYBACK_STARTED = "playback_started",
  PLAYBACK_RESUMED = "playback_resumed",
  PLAYBACK_PAUSED = "playback_paused",
  PLAYBACK_SEEKED = "playback_seeked",
  PLAYBACK_VIDEO_SETTINGS_CHANGED = "playback_video_settings_changed",
  CONSUMPTION_VIDEO_START = "consumption_video_start",
  CONSUMPTION_RESUME = "consumption_resume",
  CONSUMPTION_NINETY = "consumption_ninety",
  CUSTOMER_SUPPORT_CLICKED = "customer_support_clicked",
  APP_OPEN = "app_open",

  // search
  SEARCH_INITIATED = "search_initiated",
  SEARCH_DISPLAYED = "search_diplayed",

  SEASON_CTA_CLICKED = "season_cta_clicked",

  ADD_TO_WATCHLIST_SUCCESSFUL = "add_to_watchlist_successful",
  ADD_TO_WATCHLIST_FAILED = "add_to_watchlist_failed",

  TRAILER_AUTOPLAY_STARTED = "trailer_autoplay_started",
  VOLUME_BUTTON_CLICKED = "volume_button_clicked",
  WATCHLIST_ITEM_REMOVAL_SUCCESSFUL = "watchlist_item_removal_successful",
  WATCHLIST_ITEM_REMOVAL_FAILED = "watchlist_item_removal_failed",

  PAYWALL_VIEWED = "paywall_viewed",
  LOGIN_INITIATED = "login_initiated",
  LOGIN_INITIATED_TRUECALLER = "login_initiated_truecaller",
  LOGIN_SUCCESSFUL = "login_successful",
  PAYMENT_INITIATED_SUCCESSFUL = "payment_initiated_successful",
  PAYMENT_INITIATED = "payment_initiated",
  PAYMENT_FAILED = "payment_failed",
  START_TRIAL = "StartTrial", // dont change this value
  CAMPAIGN_CARD_VIEWED = "campaign_card_viewed",
  CAMPAIGN_CARD_CLICKED = "campaign_card_clicked",
  PAYMENT_SUCCESS_PAGE_CLICKED = "payment_success_page_clicked",

  SIGNUP_SUCCESSFUL = "signup_successful",
  TRIAL_PAYWALL_VIEWED = "trial_paywall_viewed",
  SUBSCRIPTION_PAYWALL_VIEWED = "subscription_paywall_viewed",
  TRIAL_INITIATED = "trial_initiated",
  SUBSCRIPTION_INITIATED = "subscription_initiated",
  TRIAL_PAYMENT_PAGE_VIEWED = "trial_payment_page_viewed",
  SUBSCRIPTION_PAYMENT_PAGE_VIEWED = "subscription_payment_page_viewed",
  TRIAL_PAYMENT_INITIATED = "trial_payment_initiated",
  SUBSCRIPTION_PAYMENT_INITIATED = "subscription_payment_initiated",
  TRIAL_PAYWALL_CLOSED = "trial_paywall_closed",
  SUBSCRIPTION_PAYWALL_BACK_CLICKED = "subscription_paywall_back_clicked",
  PLAN_SELECTED = "plan_selected",
  PAYMENT_SUCCESS_PAGE_VIEWED = "payment_success_page_viewed",

  TITLE_START = "title_start",
  TITLE_START_USER_CULTURE = `title_start_${DynamicEventNamesPlaceholders.USER_CULTURE}`,
  TITLE_PAGE_VIEWED = "title_page_viewed",
  TITLE_PAGE_VIEWED_USER_CULTURE = `title_page_viewed_${DynamicEventNamesPlaceholders.USER_CULTURE}`,

  //content detail
  CONTENT_DETAILS_START_OVER_BUTTON = "start_over_button",
  CONTENT_DETAILS_EPISODE_BUTTON = "episode_button",
  CONTENT_DETAILS_SIMILAR_CONTENT_BUTTON = "similar_content_button",
  CONTENT_DETAILS_TRAILER_AND_MORE_BUTTON = "trailer_and_more_button",

  // user culture popup
  USER_CULTURE_SELECTION_PAGE_VIEWED = "user_culture_selection_page_viewed",
  USER_CULTURE_SELECTION_PAGE_CLICKED = "user_culture_selection_page_clicked",
  USER_CULTURE_VALIDATION_PAGE_VIEWED = "user_culture_validation_page_viewed",
  USER_CULTURE_VALIDATION_PAGE_CLICKED = "user_culture_validation_page_clicked",
  CONTENT_CULTURE_PAGE_VIEWED = "content_culture_page_viewed",
  CONTENT_CULTURE_PAGE_CLICKED = "content_culture_page_clicked",
  USER_CULTURE_RECEIVED = "user_culture_received",
  CULTURE_SEARCH_INITIATED = "culture_search_initiated",
  CONTENT_CULTURE_UPDATED = "content_culture_updated",

  // profile selection
  PROFILE_SELECTION_PAGE_VIEWED = "profile_selection_page_viewed",
  PROFILE_CARD_SELECTED = "profile_card_selected",
  ADD_PROFILE_CTA_CLICKED = "add_profile_cta_clicked",
  EDIT_PROFILE_CTA_CLICKED = "edit_profile_cta_clicked",
  EDIT_PROFILE_CARD_CLICKED = "edit_profile_card_clicked",
  EDIT_PROFILE_PAGE_VIEWED = "edit_profile_page_viewed",
  SAVE_PROFILE = "save_profile",
  DELETE_PROFILE = "delete_profile",

  // widget
  WIDGET_VIEWED = "widget_viewed",
  THUMBNAIL_VIEWED = "thumbnail_viewed",
  WIDGET_CLICKED = "widget_clicked",
  THUMBNAIL_CLICKED = "thumbnail_clicked",
  WATCH_NOW_CLICKED = "watch_now_clicked",

  // category page
  CATEGORY_PAGE_VIEWED = "category_page_viewed",
  CATEGORY_CLICKED = "category_clicked",
  CATEGORY_DROPDOWN_VIEWED = "category_dropdown_viewed",

  // Home screen category filter events
  CONTENT_FILTER_CLICKED = "content_filter_clicked",
  CATEGORY_SELECTED = "category_selected",
  FILTER_CATEGORY_RESULT_DISPLAYED = "filter_category_result_displayed",
  FILTER_CLEARED = "filter_cleared",

  PAGE_CLICKED = "page_clicked",

  PAGE_VIEWED = "page_viewed",
  CTA_CLICKED = "cta_clicked",
  ERROR_PAGE_VIEWED = "error_page_viewed",

  // microdrama events
  EPISODE_STACK_OPENED = "episode_stack_opened",
  EPISODE_STACK_SHUFFLE = "episode_stack_shuffle",
  EPISODE_STACK_CLICKED = "episode_stack_clicked",
  MICRODRAMA_LIKED = "microdrama_liked",
  MICRODRAMA_OPTIONS_CLICKED = "microdrama_options_clicked",
  PAYMENT_CROSS_ICON_CLICKED = "payment_cross_icon_clicked",

  // payment retry modal
  BOTTOM_MODAL_VIEW = "bottom_modal_view",
  BOTTOM_MODAL_CLICK = "bottom_modal_click",
  BOTTOM_MODAL_DISMISSED = "bottom_modal_dismissed",

  // spin the wheel
  SPIN_WHEEL_VIEWED = "spin_wheel_viewed",
  SPIN_WHEEL_CLICKED = "spin_wheel_clicked",
  OFFER_CLAIMED = "offer_claimed",
  TITLE_OFFER_CLICKED = "title_offer_clicked",

  // character chat
  CHARACTER_CHAT_SESSION_CREATED = "character_chat_session_created",
  CHARACTER_CHAT_SESSION_FAILED = "character_chat_session_failed",
  CHARACTER_CHAT_MESSAGE_SENT = "character_chat_message_sent",
  CHARACTER_CHAT_MESSAGE_FAILED = "character_chat_message_failed",
  CHARACTER_CHAT_RECOMMENDATION_CARD_CLICKED = "character_chat_recommendation_card_clicked",
  CHARACTER_CHAT_SESSION_ENDED = "character_chat_session_ended",
  CONTENT_TILE_CLICKED = "content_tile_clicked",
  CHARACTER_TILE_CLICKED = "character_tile_clicked",
  WATCH_NOW_WIDGET_CLICKED = "watch_now_widget_clicked",
  BACK_BUTTON_CLICKED = "back_button_clicked",
  // hammer crack
  HAMMER_CRACK_VIEWED = "hammer_crack_viewed",
  HAMMER_CRACK_CLICKED = "hammer_crack_clicked",

  // Special Access Events
  SPECIAL_ACCESS_OFFER_VIEW = "special_access_offer_view",
  SPECIAL_ACCESS_OFFER_CLICK = "special_access_offer_click",
  SPECIAL_ACCESS_WIDGET_VIEW = "special_access_widget_view",
  SPECIAL_ACCESS_WIDGET_CLICKED = "special_access_widget_clicked",

  // Tambola
  TAMBOLA_CTA_CLICKED = "tambola_cta_clicked",

  // Stage Music
  STAGE_MUSIC_CTA_CLICKED = "stage_music_cta_clicked",
}

// Category Page Filter Values
export enum CategoryFilterEnum {
  ALL = "all",
  MOVIES = "movies",
  SHOWS = "shows",
}

// CTA Names for Analytics Events
export enum CtaNameEnum {
  CATEGORY_SELECTED = "category_selected",
  CATEGORY_TYPE_FILTER = "category_type_filter",
}

// Home filter variant for A/B testing
export enum HomeFilterVariantEnum {
  CATEGORY_FILTER_BOTTOM = "category_filter_bottom",
  CATEGORY_FILTER_BOTTOM_ICON = "category_filter_bottom_icon",
  CATEGORY_FILTER_TOP = "category_filter_top",
  CATEGORY_FILTER_TOP_ICON = "category_filter_top_icon",
}

// Content type filter values for analytics
export enum ContentTypeFilterValueEnum {
  MOVIES = "movies",
  SHOWS = "shows",
  MICRODRAMA = "microdrama",
  CATEGORIES = "categories",
}

// Filter clear method values for analytics
export enum FilterClearMethodEnum {
  CROSS_BUTTON = "cross_button",
  FILTER_TAP = "filter_tap",
  BACK_NAVIGATION = "back_navigation",
  BOTTOM_NAVBAR_HOME = "bottom_navbar_home",
}

export enum FacebookStandardEvents {
  ADD_TO_CART = "AddToCart",
  INITIATE_CHECKOUT = "InitiateCheckout",
  VIEW_CONTENT = "ViewContent",
  COMPLETE_REGISTRATION = "CompleteRegistration",
}

export enum GAEventNamesEnum {
  SIGN_UP = "sign_up",
  ADD_TO_CART = "add_to_cart",
  BEGIN_CHECKOUT = "begin_checkout",
  LOGIN = "login",
}

export enum AppsFlyerStandardEvents {
  ADD_TO_CART = "af_add_to_cart",
  INITIATED_CHECKOUT = "af_initiated_checkout",
  PURCHASE = "af_purchase",
}

export enum EventPropertiesEnum {
  PAGE_URL = "page_url",
  UTM_SOURCE = "utm_source",
  UTM_MEDIUM = "utm_medium",
  UTM_CAMPAIGN = "utm_campaign",
  SEARCH_INITIATED_KEYWORD = "search_initiated_keyword",
  RESULT_COUNT = "result_count",
  SUGGESTIONS_DISPLAYED = "suggestions_displayed",
  KEYWORD = "keyword",
  SEARCH_MODE = "search_mode",
  RESULT_DISPLAYED = "result_displayed",
  WATCH_NOW_CLICKED = "watch_now_clicked",
  SETTINGS_QUALITY = "settings_quality",
  SETTINGS_SUBTITLES = "settings_subtitles",
  SETTINGS_SPEED = "settings_speed",
  PHONE_TYPING = "ph_typing",
  LOGIN_METHOD_PHONE = "phone",
  CUSTOMER_USER_ID = "customer_user_id",
  CURRENCY = "currency",
  COUNTRY = "country",
  CITY = "city",
  APP_LANGUAGE = "app_language",
  CONTENT_ID = "content_id",
  LATEST_CONTENT_ID = "latest_content_id",
  PAGE_SOURCE = "page_source",
  CTA_NAME = "cta_name",
  ACTION_SOURCE = "action_source",
  TARGET_PROFILE_ID = "target_profile_id",
  TARGET_PROFILE_NAME = "target_profile_name",
  ACTION = "action",
  SUBSCRIPTION_STATUS = "subscription_status",
  MANDATE_STATUS = "mandate_status",

  // for Facebook Pixel
  IP_ADDRESS = "ip",
  EVENT_ID = "event_id",
  FBC = "fbc",
  FBP = "fbp",
  CONTENT_IDS = "content_ids",
  VALUE = "value",

  // for Google Ads
  ITEM_ID = "item_id", // content_id
  ECOMMERCE = "ecommerce",

  // for Saathi support
  SAATHI_PHONE = "saathi_phone",
  SAATHI_EMAIL = "saathi_email",

  // Special Access Properties
  WIDGET_TYPE = "widget_type",
  WIDGET_SOURCE = "widget_source",
  CONTENT_SLUG = "content_slug",
  SOURCE_SURFACE = "source_surface",

  // Character Chat Properties
  PAGE_NAME = "page_name",
  TOTAL_CONTENTS = "total_contents",
  TOTAL_CHARACTERS = "total_characters",
  CONTENT_TYPE = "content_type",
  SLUG = "slug",
  CONTENT_TITLE = "content_title",
  POSITION_IN_LIST = "position_in_list",
  CHARACTER_ID = "character_id",
  CHARACTER_SLUG = "character_slug",
  CHARACTER_NAME = "character_name",
  MESSAGE_ID = "message_id",
  SOURCE = "source",
  FROM_PAGE = "from_page",
  SESSION_DURATION_SECONDS = "session_duration_seconds",
  MESSAGE_COUNT = "message_count",
  EXIT_CHAT = "exit_chat",
  CHAT_INTERACTION_STATUS = "chat_interaction_status",
  APP_VERSION = "app_version",
  MESSAGE_LENGTH = "message_length",
  ERROR = "error",
  // Identity duplicates (excluded from analytics — redundant with user_id)
  EXTERNAL_ID = "external_id",
  PH = "ph",
  USER_ID_LEGACY = "userId",

  // Content properties (derivable from dim_content via content_id join)
  CONTENT_NAME = "content_name",
  CONTENT_DIALECT = "content_dialect",
  SHOW_ID = "show_id",

  // Zero-variance / framework artifacts
  NEXT_JS_WEBSITE = "nextJsWebsite",
  SDK_VERSION = "version",
  CHANNEL = "channel",
  SHOW_FORMAT = "showFormat",

  // Redundant / low-signal user/content attributes
  DEVICE_SCREEN_TYPE = "device_screen_type",
  CONTENT_CULTURE = "content_culture",
  INITIAL_USER_CULTURE = "initial_user_culture",
  USER_CULTURE = "user_culture",

  // Dead properties (0% fill — never populated on web)
  APP_DISPLAY_LANGUAGE = "app_display_language",
  PAGE_TITLE = "page_title",
}

// Special Access Widget Types
export enum SpecialAccessWidgetTypeEnum {
  FLOATING_BANNER = "floating_banner",
  COMING_UP_DRAWER = "coming_up_drawer",
  CONTINUE_WATCHING = "continue_watching_tray",
  HOME_PLATTER = "home_platter",
}

// Special Access Source Names
export enum SpecialAccessSourceEnum {
  SPECIAL_ACCESS = "special_access",
  CONTINUE_WATCHING_TRAY = "continue_watching_tray",
  HOME_PAGE_PLATTER = "home_page_platter",
  SPECIAL_ACCESS_COMING_UP_DRAWER = "special_access_coming_up_drawer",
}

// TODO: remove this once dynamic homepage done
export enum SourceWidgetsEnum {
  PLATTER = "platter",
  TOP20CONTENT = "top20Content",
  RECOMMENDED_FOR_YOU = "recommendedForYou",
  TRENDING_CONTENT = "trendingContent",
  SIMILAR_SHOWS = "similarShows",
  PREMIUM_SHOWS = "premiumShows",
  NEW_SHOWS = "newShows",
  WEB_SERIES = "webSeries",
  DEEPLINK = "deeplink",
  CONTINUE_WATCHING = "continueWatching",
  SEARCH_RESULT = "searchResult",
  TRENDING_SEARCH = "trendingSearch",
  MY_LIST = "myList",
  GENRE = "genre",
  SHOWS = "Shows",
  MOVIES = "Movies",
  MICRO_DRAMA = "microDrama",
  // Non-widget page surfaces (used as source_surface on cta_clicked)
  TITLE_DETAIL = "title_detail",
  EPISODE_LISTING = "episode_listing",
  SEASON_SWITCH = "season_switch",
  TRAILER_PLAYER = "trailer_player",
  SIMILAR_CONTENT = "similar_content",
}

export const sourceWidgetValues: {
  [key in SourceWidgetsEnum]: string;
} = {
  [SourceWidgetsEnum.PLATTER]: "Platter",
  [SourceWidgetsEnum.TOP20CONTENT]: "Top 20 Content",
  [SourceWidgetsEnum.RECOMMENDED_FOR_YOU]: "Recommended For You",
  [SourceWidgetsEnum.TRENDING_CONTENT]: "Trending Content",
  [SourceWidgetsEnum.SIMILAR_SHOWS]: "Similar Shows",
  [SourceWidgetsEnum.PREMIUM_SHOWS]: "Premium Shows",
  [SourceWidgetsEnum.NEW_SHOWS]: "New Shows",
  [SourceWidgetsEnum.WEB_SERIES]: "Web Series",
  [SourceWidgetsEnum.DEEPLINK]: "Deeplink",
  [SourceWidgetsEnum.CONTINUE_WATCHING]: "Continue Watching",
  [SourceWidgetsEnum.SEARCH_RESULT]: "Search Result",
  [SourceWidgetsEnum.TRENDING_SEARCH]: "Trending Search",
  [SourceWidgetsEnum.MY_LIST]: "My List",
  [SourceWidgetsEnum.GENRE]: "Genre",
  [SourceWidgetsEnum.SHOWS]: "Shows",
  [SourceWidgetsEnum.MOVIES]: "Movies",
  [SourceWidgetsEnum.MICRO_DRAMA]: "Micro Drama",
  [SourceWidgetsEnum.TITLE_DETAIL]: "Title Detail",
  [SourceWidgetsEnum.EPISODE_LISTING]: "Episode Listing",
  [SourceWidgetsEnum.SEASON_SWITCH]: "Season Switch",
  [SourceWidgetsEnum.TRAILER_PLAYER]: "Trailer Player",
  [SourceWidgetsEnum.SIMILAR_CONTENT]: "Similar Content",
};

/**
 * CTA name values for the unified cta_clicked event.
 * Use this instead of EventNamesEnum for the cta_name property to keep
 * event names and CTA action labels semantically separate.
 * Values derived from EventNamesEnum to prevent drift and preserve Snowflake
 * column continuity with existing CTA_CLICKED_WEB / CTA_CLICKED_TV data.
 */
export enum CtaNameEnum {
  WATCH_NOW = EventNamesEnum.WATCH_NOW_CLICKED,
  START_WATCHING = EventNamesEnum.START_WATCHING_CLICKED,
  EPISODE_TILE = EventNamesEnum.EPISODE_TILE_CLICKED,
  SEASON_CTA = EventNamesEnum.SEASON_CTA_CLICKED,
  TRAILER_PLAY = EventNamesEnum.TRAILER_PLAY_CTA_CLICKED,
  SIMILAR_CONTENT = EventNamesEnum.SIMILAR_CONTENT_CLICKED,
}

export enum SourcePagesEnum {
  HOME = "home",
  CONSUMPTION = "watch",
  CONTENT_DETAIL = "contentDetail",
  DEEPLINK = "deeplink",
  All_SHOWS = "all_shows",
  ALL_MOVIES = "all_movies",
  SEARCH = "search",
  MY_LIST = "my_list",
  GENRE_DETAIL = "genre_detail",
  GENRE_LISTING = "genre_listing",
  SHOWS = "shows",
  MOVIES = "movies",
  PAYMENT_FAILED = "payment_failed",
  PROFILE_SELECTION = "profile_selection",
  PROFILE_SELECTION_PAGE = "profile_selection_page",
  EDIT_PROFILE_PAGE = "edit_profile_page",
  CREATE_PROFILE_PAGE = "create_profile_page",
  CREATE = "create",
  UPDATE = "update",
  SETTINGS = "settings",
  LOGIN = "login",
  PAYWALL = "paywall",
  TITLE = "title",
  SPIN_THE_WHEEL = "spin_the_wheel",
  WATCH = "watch",
  HAMMER_CRACK = "hammer_crack",
  HYBRID_PAYWALL = "hybrid_paywall",
  CHARACTERS_HOME_PAGE = "home_page",
  CHARACTERS_TITLE_PAGE = "title_page",
}

export enum ChatInteractionStatusEnum {
  NOT_INITIATED = "not_initiated",
  INITIATED = "initiated",
  CHAT_LEFT_MIDWAY = "chat_left_midway",
  CHAT_DONE = "chat_done",
}

export enum ContentDetailPageEnum {
  EPISODES_BUTTON = "EPISODES_BUTTON",
  WATCH_TRAILER_BUTTON = "WATCH_TRAILER_BUTTON",
  MORE_LIKE_THIS_BUTTON = "MORE_LIKE_THIS_BUTTON",
  TRAILERS_AND_MORE_BUTTON = "TRAILERS_AND_MORE_BUTTON",
  PLAY_FROM_BEGINNING_BUTTON = "PLAY_FROM_BEGINNING_BUTTON",
  PRIMARY_WATCH_BUTTON = "PRIMARY_WATCH_BUTTON",
  CONTENT_DETAIL_SCREEN = "CONTENT_DETAIL_SCREEN",
  CONTENT_DETAIL_TRAILER_WIDGET_TV = "CONTENT_DETAIL_TRAILER_WIDGET_TV",
}
export const sourcePagesValues: {
  [key in SourcePagesEnum]: string;
} = {
  [SourcePagesEnum.HOME]: "Home",
  [SourcePagesEnum.CONSUMPTION]: "Watch",
  [SourcePagesEnum.CONTENT_DETAIL]: "Content Detail",
  [SourcePagesEnum.DEEPLINK]: "Deeplink",
  [SourcePagesEnum.ALL_MOVIES]: "All Movies",
  [SourcePagesEnum.All_SHOWS]: "All Shows",
  [SourcePagesEnum.SEARCH]: "Search",
  [SourcePagesEnum.MY_LIST]: "My List",
  [SourcePagesEnum.GENRE_DETAIL]: "Genre Detail",
  [SourcePagesEnum.GENRE_LISTING]: "Genre Listing",
  [SourcePagesEnum.SHOWS]: "Shows",
  [SourcePagesEnum.MOVIES]: "Movies",
  [SourcePagesEnum.PAYMENT_FAILED]: "Payment Failed",
  [SourcePagesEnum.PROFILE_SELECTION]: "Profile Selection",
  [SourcePagesEnum.PROFILE_SELECTION_PAGE]: "Profile Selection Page",
  [SourcePagesEnum.EDIT_PROFILE_PAGE]: "Edit Profile Page",
  [SourcePagesEnum.CREATE_PROFILE_PAGE]: "Create Profile Page",
  [SourcePagesEnum.CREATE]: "Create",
  [SourcePagesEnum.UPDATE]: "Update",
  [SourcePagesEnum.SETTINGS]: "Settings",
  [SourcePagesEnum.LOGIN]: "Login",
  [SourcePagesEnum.PAYWALL]: "Paywall",
  [SourcePagesEnum.TITLE]: "Title",
  [SourcePagesEnum.SPIN_THE_WHEEL]: "Spin The Wheel",
  [SourcePagesEnum.HAMMER_CRACK]: "Hammer Crack",
  [SourcePagesEnum.HYBRID_PAYWALL]: "Hybrid Paywall",
  [SourcePagesEnum.CHARACTERS_HOME_PAGE]: "Characters Home Page",
  [SourcePagesEnum.CHARACTERS_TITLE_PAGE]: "Characters Title Page",
};

export enum PageViewedEventPageNameEnum {
  stageOnTv = "StageOnTvPage",
  contentDetail = "ContentDetailScreen",
  myList = "MyListScreen",
  paymentFailed = "PaymentFailed",
  paymentSuccess = "PaymentSuccess",
  notFoundPage = "NotFoundPage",
  globalErrorPage = "GlobalErrorPage",
  globalCharactersList = "GlobalCharactersListPage",
  showCharactersList = "ShowCharactersListPage",
  characterChat = "CharacterChatPage",
}

export enum WatchVideoAPIEvents {
  PLAY = "play",
  PAUSE = "pause",
  STOP = "stop",
}

export enum DisplayTagsEnum {
  MICRO_DRAMA = "DRAMA SHORTS",
}
export enum SearchStageEnum {
  TAP = "tap",
  TYPING = "typing",
}

export enum ThumbnailTypeEnum {
  VIDEO = "video",
  PHOTO = "photo",
}

// Content Detail Page CTA Name Mapping
export const CONTENT_DETAIL_CTA_NAME_MAP: Record<string, string> = {
  MORE_LIKE_THIS_BUTTON: EventNamesEnum.CONTENT_DETAILS_SIMILAR_CONTENT_BUTTON,
  EPISODES_BUTTON: EventNamesEnum.CONTENT_DETAILS_EPISODE_BUTTON,
  PLAY_FROM_BEGINNING_BUTTON: EventNamesEnum.CONTENT_DETAILS_START_OVER_BUTTON,
  TRAILERS_AND_MORE_BUTTON:
    EventNamesEnum.CONTENT_DETAILS_TRAILER_AND_MORE_BUTTON,
} as const;

export enum AvoidIntersectingEventsEnum {
  LEFT_ARROW = "ArrowLeft",
  RIGHT_ARROW = "ArrowRight",
  ENTER = "Enter",
}

export enum VideoPlayerNameEnum {
  HLS_JS_H265 = "HLS.js-H265",
  HLS_JS = "HLS.js",
}

export enum VideoReadyState {
  HAVE_NOTHING = 0,
  HAVE_METADATA = 1,
  HAVE_CURRENT_DATA = 2,
  HAVE_FUTURE_DATA = 3,
  HAVE_ENOUGH_DATA = 4,
}

export enum MediaNameEnum {
  INSTAGRAM = "Instagram",
  FACEBOOK = "Facebook",
  YOUTUBE = "YouTube",
}

export const MAX_FOOTER_LINKS_COUNT = 5;

export const DEFAULT_LOGIN_FLOW_VARIANT = "login_with_image";

export enum LoginPageStateEnum {
  SEND_OTP = "sendOTP",
  VERIFY_OTP = "verifyOTP",
}

export enum PaywallMediaAssetsTypeEnum {
  IMAGE = "image",
  VIDEO = "video",
}

export enum AppRoutesEnum {
  COMMON_HOME = "/",
  LOGIN = "/login",
  SELECT_CULTURE = "/select-culture",
  APP_SETTINGS = "/app-settings",
  PAYWALL = "/paywall",
  CHECKOUT = "/checkout",
  SEARCH = "/search",
  MOVIES = "/movies",
  SHOWS = "/shows",
  MY_LIST = "/my-list",
  SETTINGS = "/settings",
  CONTACT_US = "/contact-us",
  GENRE = "/genre",
  TITLE = "/title",
  WATCH = "/watch",
  SPIN_THE_WHEEL = "/paywall/spin-the-wheel",
  CHARACTER_CHAT = "/characters/chat",
  SPECIAL_ACCESS = "/paywall/special-access",
  MICRODRAMA = "/microdrama",
  REFUND_POLICY = "/refund-policy",
  FAQ = "/faq",
}

// Auto-generated from DIALECT_CONFIGS - adds new dialects automatically
export const ContentToUserCultureMap: {
  [k in DialectsEnum]: string;
} = generateContentToUserCultureMap();

//       _        _       _
//      | |      | |     (_)
//   ___| |_ __ _| |_ ___ _  __ _
//  / __| __/ _` | __/ __| |/ _` |
//  \__ \ || (_| | |_\__ \ | (_| |
//  |___/\__\__,_|\__|___/_|\__, |
//                           __/ |
//                          |___/

export enum StatsigLayerEnum {
  TRIAL_PLAN = "web_plan_id_test_experiment",
  CROSS_ICON_PAYWALL = "cross_icon_paywall",
  TRUECALLER = "truecaller_experiment",
  PAYMENT_FAILED_FLOW_EXPERIMENT = "payment_failed_flow_experiment",
  PAYMENT_RETRY_MODAL = "payment_retry_modal_experiment",
  SPIN_THE_WHEEL = "spin_the_wheel",
  WEB_SPECIAL_ACCESS = "web_special_access",
  SCARCITY_TIMER = "scarcity_timer",
  CROSS_ICON_PAYMENT_SUCCESS = "cross_icon_payment_success",
  HYBRID_PAYWALL = "hybrid_paywall_experiment",
  PAYMENT_SUCCESS_APP_REDIRECTION = "payment_success_app_redirection",

  // Dynamic Configs
  PAYWALL_ASSET_CLICK = "paywall_asset_click",
  PAYMENT_SUCCESS_PAGE = "new_payment_success_page",
}

export enum StatsigLayerParametersEnum {
  PLAN_ID = "planId",
  IS_DEEPLINK_PRIORITY = "isDeeplinkPriority",
  UPDATE_PLAN_FOR_VERSION_BELOW = "updatePlanForVersionBelow",
  SHOW_CROSS_ICON = "showCrossIcon",
  SHOW_TC_BY_DEFAULT = "showTCByDefault",
  SHOW_PAYMENT_FAIL_PAGE = "showPaymentFailPage",
  SHOW_PAYMENT_RETRY_MODAL = "showPaymentRetryModal",
  ENABLE_SPIN_THE_WHEEL = "enableSpinTheWheel",
  IS_SPECIAL_ACCESS_ENABLED = "isSpecialAccessEnabled",
  ENABLE_TIMER = "enableTimer",
  SHOW_CROSS_ICON_PAYMENT_SUCCESS = "showCrossIconPaymentSuccess",
  REDIRECTION_TO_APP = "redirectionToApp",

  // Hybrid Paywall Experiment
  HYBRID_PAYWALL_VARIANT = "variant",
  HYBRID_PAYWALL_HERO_ASSET = "heroAsset",

  // Dynamic Configs
  IS_PAYWALL_ASSET_CLICK = "is_paywall_asset_click",
  SHOW_NEW_PAYMENT_SUCCESS_PAGE = "show_new_payment_success_page",
}

export const statsigConfigDefaultValues: {
  [k in StatsigLayerParametersEnum]: string | boolean | number;
} = {
  [StatsigLayerParametersEnum.PLAN_ID]: "",
  [StatsigLayerParametersEnum.IS_DEEPLINK_PRIORITY]: true,
  [StatsigLayerParametersEnum.UPDATE_PLAN_FOR_VERSION_BELOW]: 0,
  [StatsigLayerParametersEnum.SHOW_CROSS_ICON]: false,
  [StatsigLayerParametersEnum.SHOW_TC_BY_DEFAULT]: true,
  [StatsigLayerParametersEnum.SHOW_PAYMENT_FAIL_PAGE]: false,
  [StatsigLayerParametersEnum.SHOW_PAYMENT_RETRY_MODAL]: false,
  [StatsigLayerParametersEnum.ENABLE_SPIN_THE_WHEEL]: false,
  [StatsigLayerParametersEnum.IS_SPECIAL_ACCESS_ENABLED]: true,
  [StatsigLayerParametersEnum.ENABLE_TIMER]: false,
  [StatsigLayerParametersEnum.SHOW_CROSS_ICON_PAYMENT_SUCCESS]: false,
  [StatsigLayerParametersEnum.REDIRECTION_TO_APP]: false,

  // Hybrid Paywall Experiment
  [StatsigLayerParametersEnum.HYBRID_PAYWALL_VARIANT]: "generic-paywall", // default variant:: DO NOT CHANGE THIS VALUE
  [StatsigLayerParametersEnum.HYBRID_PAYWALL_HERO_ASSET]: "",

  // Dynamic Configs
  [StatsigLayerParametersEnum.IS_PAYWALL_ASSET_CLICK]: false,
  [StatsigLayerParametersEnum.SHOW_NEW_PAYMENT_SUCCESS_PAGE]: true,
};
export enum MicroDramaInteractionActionEnum {
  LIKE = "like",
}

// MicroDrama constants
export const SWIPE_THRESHOLD = 50;
export const VELOCITY_THRESHOLD = 0.5;

//       _         _ _
//   ___| |_ _   _| (_)_ __   __ _
//  / __| __| | | | | | '_ \ / _` |
//  \__ \ |_| |_| | | | | | | (_| |
//  |___/\__|\__, |_|_|_| |_|\__, |
//           |___/           |___/

export const shimmerTailwindClasses =
  "absolute inset-0 animate-shimmer bg-gradient-to-r from-[var(--grey-3)] via-[var(--grey-4)] to-[var(--grey-3)] -translate-x-full";

// App Download Banner Config
export const APP_DOWNLOAD_SCROLL_BANNER_TIMEOUT = 200;
export const APP_BANNER_ANIMATE_IN_TIMEOUT = 500;
export const APP_DOWNLOAD_SIDE_BANNER_COLLAPSE_TIMEOUT = 5000;

// Start Trial Banner Config
export const START_TRIAL_ANIMATE_IN_TIMEOUT = 200;

// Special Access Banner Config
export const SPECIAL_ACCESS_BANNER_ANIMATE_IN_TIMEOUT = 200;

// Special Access Offer Page Animation Timeouts
export const SPECIAL_ACCESS_OFFER_BLUR_EFFECT_DELAY_MS = 500;
export const SPECIAL_ACCESS_OFFER_POST_ANIMATION_CONTENT_DELAY_MS = 1000;
export const SPECIAL_ACCESS_OFFER_CONFETTI_SHOW_DELAY_MS = 700;
export const SPECIAL_ACCESS_OFFER_CONFETTI_HIDE_DELAY_MS = 3000;

export const LoginPageEnums = {
  OTP: "OTP",
};

// Subscription Defaults
export const DEFAULT_SUBSCRIPTION_PRICE = 399;
export const DEFAULT_SUBSCRIPTION_VALIDITY = "Quarterly";
export const DEFAULT_SUBSCRIPTION_DAYS = 90;
export const DEFAULT_INDEPENDENCE_DAY_SUBSCRIPTION_PRICE = 79;
// default values for diwali offer
export const DEFAULT_DIWALI_OFFER_SUBSCRIPTION_PRICE = 51;
export const DEFAULT_DIWALI_OFFER_SUBSCRIPTION_VALIDITY = "Quarterly";
export const DEFAULT_DIWALI_OFFER_SUBSCRIPTION_DAYS = 90;

export const SRT_PARSER_REGEX = {
  COMMA:
    /(\d+)\n(\d{1,2}:\d{2}:\d{2},\d{1,3}) --> (\d{1,2}:\d{2}:\d{2},\d{1,3})/g,
  DOT: /(\d+)\n(\d{1,2}:\d{2}:\d{2}\.\d{1,3}) --> (\d{1,2}:\d{2}:\d{2}\.\d{1,3})/g,
  EOL: "\r\n",
};

export const PAYMENT_EVENT_PROPERTIES_VALUES = {
  QR_CORE: "QR Code",
  UPI: "upi",
  DESKTOP_QR: "desktop_qr",
};

export enum PageClickedCTAEnum {
  CLOSE = "close",
  STOP_COUNTDOWN = "stop_countdown",
  AUTO_REDIRECT = "auto_redirect",
  START_WATCHING = "start_watching",
}

export const HOME_PAGE_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Stage.in",
  url: "https://stage.in",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: STAGE_CONTACT_NUMBER,
    contactType: "customer service",
    areaServed: "IN",
    availableLanguage: ["Hindi", "English, Haryanvi, Rajasthani, Bhojpuri"],
  },
};

export const CONTACT_US_PAGE_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Stage.in",
  url: "https://stage.in",
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: STAGE_CONTACT_NUMBER,
      contactType: "customer service",
      areaServed: "IN",
      availableLanguage: [
        "Hindi",
        "English",
        "Haryanvi",
        "Rajasthani",
        "Bhojpuri",
      ],
    },
    {
      "@type": "ContactPoint",
      email: "support@stage.in",
      contactType: "customer service",
      areaServed: "IN",
      availableLanguage: [
        "Hindi",
        "English",
        "Haryanvi",
        "Rajasthani",
        "Bhojpuri",
      ],
    },
    {
      "@type": "ContactPoint",
      email: "cgo@stage.in",
      contactType: "customer complaints",
      areaServed: "IN",
      availableLanguage: [
        "Hindi",
        "English",
        "Haryanvi",
        "Rajasthani",
        "Bhojpuri",
      ],
    },
  ],
};

export enum PageNameEnum {
  PAYWALL = "paywall",
  HOME = "home_page",
  TITLE = "new_title_page",
  WATCH = "watch_page",
  CONTENT_DETAIL = "content_detail_page",
}

export const STW_CLAIM_TIME_EXPIRY_TIME_IN_SECONDS = 300;
export const GAMIFICATION_CLAIM_TIME_EXPIRY_SECONDS = 300;

export enum PaywallPageCookieEnum {
  SPIN_THE_WHEEL = "spin-the-wheel",
  HAMMER_CRACK = "hammer-crack",
}

export enum FlutterOutgoingMessageType {
  READY = "ready",
  REQUEST_DATA = "requestData",
  NAVIGATION = "navigation",
  OPEN_WHATSAPP = "openWhatsApp",
  MANAGE_SUBSCRIPTION = "manageSubscription",
  REGISTER_TV = "registerTv",
  TV_SETUP_GUIDE = "tvSetupGuide",
  UPDATE_CULTURE = "updateCulture",
  UPDATE_LANGUAGE = "updateLanguage",
  UPDATE = "update",
  OPEN_URL = "openUrl",
  LOGOUT = "logout",
  OPEN_SUPPORT = "openSupport",
  CLOSE = "close",
}

// Paywall Gamification Features
export enum GamificationPlanIdEnum {
  SPIN_THE_WHEEL = "trial-spin-the-wheel",
  HAMMER_CRACK = "trial-hammer-crack",
  SPECIAL_ACCESS = "trial-special-access",
}

// Route-friendly names for paywall features
export const GAMIFICATION_PLAN_ID_TO_ROUTE: Record<
  GamificationPlanIdEnum,
  string
> = {
  [GamificationPlanIdEnum.SPIN_THE_WHEEL]: "spin-the-wheel",
  [GamificationPlanIdEnum.HAMMER_CRACK]: "hammer-crack",
  [GamificationPlanIdEnum.SPECIAL_ACCESS]: "special-access",
};
